import React, { useEffect, useRef, useState } from 'react';

import { Discipline } from '../../core/typings';
import Countdown from '../Countdown';
import EventDate from '../EventDate';
import EventLocation from '../EventLocation';
import { Grid } from '../helpers/Grid';
import WatchProps from '../Watch/type';
import Watch from '../Watch';
import { Image, Link } from '../../typings';
import { HeroBanner, ImageObject, SvgImage } from '../common';
import './styles.scss';

export interface RaceHeaderProps {
  backLink?: Link | null;
  broadcasterLogoImage?: Image | null;
  competitionImage?: Image | null;
  competitionTitle?: string;
  countdownDate?: string;
  discipline?: Discipline | null;
  heroImage: Image;
  raceTitle: string;
  raceStartDate?: string;
  raceEndDate?: string | null;
  raceStartLocation: string;
  raceEndLocation?: string | null;
  state: 'pre' | 'live' | 'post';
  timekeeperLogoImage?: Image | null;
  watchOverlay?: WatchProps;
}

const RaceHeader = ({
  backLink,
  broadcasterLogoImage,
  competitionImage,
  competitionTitle,
  countdownDate,
  discipline,
  heroImage,
  raceTitle,
  raceStartDate,
  raceEndDate,
  raceStartLocation,
  raceEndLocation,
  state,
  timekeeperLogoImage,
  watchOverlay,
}: RaceHeaderProps) => {
  const root = 'race-header';
  const classes = [
    root,
    `${root}--${state}`,
  ];
  if (discipline) {
    classes.push(`${root}--discipline-${discipline}`);
  }

  const [showWatchOverlay, setShowWatchOverlay] = useState(false);
  const closeOverlayBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showWatchOverlay) {
      closeOverlayBtnRef.current?.focus();
    }
  }, [showWatchOverlay]);

  if (showWatchOverlay) {
    classes.push(`${root}--has-overlay`);
  }

  // TODO: l10n "watch on" / "watch live" / "highlights"
  return (
    <div className={classes.join(' ')}>
      <HeroBanner
        backLink={backLink}
        colorTheme="dark-grey"
        image={heroImage}
        imageSize="full"
        isDarkened
        ariaHidden={showWatchOverlay}
      >
        {competitionImage && (
          <div className={`${root}__competition-logo`}>
            <ImageObject image={competitionImage} />
          </div>
        )}

        <div className={`${root}__race-info`}>
          {!!competitionTitle && (
            <h1 className={`${root}__race-info__competition`}>
              {competitionTitle}
            </h1>
          )}
          <h2 className={`${root}__race-info__title`}>
            {raceTitle}
          </h2>

          {raceStartDate && (
            <EventDate startDate={raceStartDate} endDate={raceEndDate || undefined} />
          )}

          <div className={`${root}__race-info__watch`}>
            {state === 'live' && watchOverlay && (
              <button
                type="button"
                onClick={() => setShowWatchOverlay(true)}
              >
                <SvgImage ariaHidden reference="#icon-play-small" />
                Watch live
              </button>
            )}
            {state === 'post' && watchOverlay && (
              <button
                type="button"
                onClick={() => setShowWatchOverlay(true)}
              >
                <SvgImage ariaHidden reference="#icon-play-small" />
                Highlights
              </button>
            )}
          </div>

          <div className={`${root}__race-info__bottom`}>
            <Grid>
              <Grid.Column sm={6} md={8} lg={4} xl={6} className={`${root}__race-info__bottom__location`}>
                <EventLocation
                  startLocation={raceStartLocation}
                  endLocation={raceEndLocation || undefined}
                />
              </Grid.Column>
              {state === 'pre' && countdownDate && (
                <Grid.Column sm={6} md={8} lg={4} xl={6} className={`${root}__race-info__bottom__countdown`}>
                  <Countdown timestamp={countdownDate} discipline={discipline} />
                  <div className={`${root}__race-info__bottom__countdown__timekeeper-logo`}>
                    {timekeeperLogoImage && <ImageObject image={timekeeperLogoImage} fitType="contain" />}
                  </div>
                </Grid.Column>
              )}
            </Grid>

            {broadcasterLogoImage && (
              <div className={`${root}__race-info__bottom__broadcaster`}>
                <p>Watch on</p>
                <ImageObject image={broadcasterLogoImage} fitType="contain" />
              </div>
            )}
          </div>
        </div>
      </HeroBanner>

      <div className={`${root}__overlay`}>
        <button
          className={`${root}__overlay__close`}
          aria-label="Close"
          onClick={() => setShowWatchOverlay(false)}
          ref={closeOverlayBtnRef}
        >
          <SvgImage reference="#icon-close" ariaHidden />
        </button>

        <div className={`${root}__overlay__content`}>
          {watchOverlay && <Watch {...watchOverlay} />}
        </div>
      </div>
    </div>
  );
};

export default RaceHeader;
